/**
 * 主题模式管理（2026-06 dark mode）。
 *
 * 三态：'auto'（默认，跟随 prefers-color-scheme）/ 'light' / 'dark'。
 * 持久化 localStorage.ac_theme；应用方式为 <html class="dark">（Element Plus
 * 官方暗色变量的激活约定，颜色变量见 src/styles/theme.css）。
 *
 * 兼容性注意（65% 用户在微信 X5）：
 *   - iOS 微信 2020+ / 多数安卓 X5 支持 prefers-color-scheme；不支持时
 *     matchMedia 匹配失败 → auto 等同 light，安全降级。
 *   - 旧 X5 的 MediaQueryList 只有 addListener（无 addEventListener），两个都试。
 *
 * 模块级单例：initTheme() 在 main.js 启动时调用一次；useTheme() 供组件读写。
 */
import {ref} from 'vue'

const THEME_KEY = 'ac_theme'
const VALID_MODES = ['auto', 'light', 'dark']

const mode = ref('auto')

function _systemDark() {
  try {
    return !!(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches)
  } catch (_) {
    return false
  }
}

function _apply() {
  const dark = mode.value === 'dark' || (mode.value === 'auto' && _systemDark())
  document.documentElement.classList.toggle('dark', dark)
}

export function initTheme() {
  try {
    const saved = localStorage.getItem(THEME_KEY)
    if (VALID_MODES.includes(saved)) mode.value = saved
  } catch (_) { /* private mode / storage 禁用：维持 auto */ }
  _apply()
  try {
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = () => { if (mode.value === 'auto') _apply() }
    if (media.addEventListener) media.addEventListener('change', handler)
    else if (media.addListener) media.addListener(handler)  // 旧 X5 fallback
  } catch (_) { /* matchMedia 不可用：auto 固定为 light */ }
}

export function useTheme() {
  const setMode = (m) => {
    if (!VALID_MODES.includes(m)) return
    mode.value = m
    try { localStorage.setItem(THEME_KEY, m) } catch (_) {}
    _apply()
  }
  return {mode, setMode}
}
